import { useEffect, useState } from 'react';
import { authFetch } from '../lib/auth';
import type { AttachmentRow } from './AttachmentList';

interface Props {
  displayId: number;
  attachment: AttachmentRow;
  onClose: () => void;
}

export function AttachmentPreview({ displayId, attachment, onClose }: Props) {
  const [textContent, setTextContent] = useState<string | null>(null);

  const url = `/api/tickets/${displayId}/attachments/${encodeURIComponent(attachment.filename)}`;
  const downloadUrl = `${url}?mode=download`;

  useEffect(() => {
    if (attachment.mime_type !== 'text/plain') return;
    const ctl = new AbortController();
    authFetch(url, { signal: ctl.signal })
      .then((r) => r.text())
      .then((t) => !ctl.signal.aborted && setTextContent(t))
      .catch(() => !ctl.signal.aborted && setTextContent('[lecture impossible]'));
    return () => ctl.abort();
  }, [attachment.mime_type, url]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          borderRadius: 12,
          padding: 16,
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 480,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <strong style={{ flex: 1, fontSize: 'var(--fs-base)', wordBreak: 'break-all' }}>{attachment.original_filename}</strong>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>{attachment.mime_type}</span>
          <a href={downloadUrl} className="btn" style={{ fontSize: 'var(--fs-xs)' }}>
            ⬇ Télécharger
          </a>
          <button onClick={onClose} className="btn ghost" style={{ fontSize: 'var(--fs-md)' }}>
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-soft)', borderRadius: 6 }}>
          {attachment.mime_type.startsWith('image/') && (
            <img src={url} alt={attachment.original_filename} style={{ maxWidth: '100%', display: 'block' }} />
          )}
          {attachment.mime_type === 'application/pdf' && (
            // Pas de sandbox="" : le viewer PDF natif de Chrome est bloqué dans un
            // iframe sandboxé opaque. Sûreté = Content-Type application/pdf + nosniff
            // côté serveur (le fichier ne peut pas être réinterprété en HTML).
            <iframe
              src={url}
              title={attachment.original_filename}
              style={{ width: '80vw', maxWidth: 900, height: '75vh', border: 0 }}
            />
          )}
          {attachment.mime_type === 'text/html' && (
            // sandbox="" STRICT : origine opaque, aucun script. Le serveur renvoie
            // aussi une CSP sandbox → double neutralisation d'un HTML legacy.
            <iframe
              src={url}
              title={attachment.original_filename}
              sandbox=""
              style={{ width: '80vw', maxWidth: 900, height: '75vh', border: 0, background: '#fff' }}
            />
          )}
          {attachment.mime_type === 'text/plain' && (
            <pre
              style={{
                margin: 0,
                padding: 16,
                fontSize: 'var(--fs-xs)',
                fontFamily: 'ui-monospace, Menlo, monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '70vh',
                overflow: 'auto',
              }}
            >
              {textContent ?? 'Chargement…'}
            </pre>
          )}
          {!attachment.mime_type.startsWith('image/') &&
            attachment.mime_type !== 'application/pdf' &&
            attachment.mime_type !== 'text/plain' &&
            attachment.mime_type !== 'text/html' && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-mute)' }}>
                Preview non disponible pour ce type.
                <br />
                <a href={downloadUrl} className="btn primary" style={{ marginTop: 12, display: 'inline-block' }}>
                  ⬇ Télécharger le fichier
                </a>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
